import React, { useState, useRef, ChangeEvent, useEffect } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { Container, List, ListItem, IconSearch } from './styles';
import Icon from 'src/components/common/Icon';
import { getSearch } from './api';
import { TextField } from '@habitech/keychain-atoms-inputs';

interface SearchInputResultProps {
    error: string;
    onChange?: (val: StateProps) => any;
    initialValue: string;
}

interface StateProps {
    id: number | string;
    label: string;
}

export const SearchInputResult: React.FC<SearchInputResultProps> = ({ error, onChange, initialValue }) => {
    const defaultPlaceholder = 'Busca por estado, colonia o delegación';
    const inputRef = useRef<HTMLInputElement | null>(null);
    const [colonies, setColonies] = useState<StateProps[]>([]);
    const [value, setValue] = useState<string>('');
    const [fullWidth, setFullWidth] = useState<boolean>(false);
    const [isFocus, setIsFocus] = useState<boolean>(false);
    const [errorText, setErrorText] = useState(error);
    let ajaxRequest: CancelTokenSource | null = null;

    const handleOnChange = async (event: ChangeEvent<HTMLInputElement>) => {
        /* cancel previous ajax if exists */
        if (ajaxRequest) {
            ajaxRequest.cancel();
        }

        /* creates a new token for upcoming ajax (overwrite the previous one) */
        ajaxRequest = axios.CancelToken.source();
        const searchValue = event.target.value;
        setValue(searchValue);

        if (searchValue.length > 3) {
            getData(searchValue);
        } else {
            setColonies([]);
        }
    };

    const getData = async (searchValue: string) => {
        try {
            const res = await getSearch(searchValue);
            setColonies(res);
        } catch (error) {
            console.error(error);
        }
    };

    const onClickLocation = (colony: StateProps) => {
        const { label } = colony;
        onChange?.(colony);
        setValue(label);
        setColonies([]);
        setErrorText('');
    };

    const handleClearSearch = (e: React.MouseEvent) => {
        e.preventDefault();
        setValue('');
        setIsFocus(false);
        onChange?.({ id: '', label: '' });
        if (inputRef.current) {
            inputRef.current.value = '';
            inputRef.current.focus();
        }
    };

    const handleFullWidth = (e: React.MouseEvent, _fullwidth: boolean) => {
        e.preventDefault();

        if (!_fullwidth) {
            if (inputRef.current) {
                inputRef.current.value = '';
                inputRef.current.blur();
            }
            setColonies([]);
            setFullWidth(false);
            setIsFocus(false);
            setValue('');
        }

        // only if the search input has not been opened
        if (!fullWidth && _fullwidth) {
            setFullWidth(true);
            setIsFocus(true);
        }
    };

    const onKeyUp = (e) => {
        // Action for the Enter key
        if (e.charCode === 13) {
            setFirstItem();
        }
        // Action for the Tab key
        if (e.keyCode === 9) {
            handleFullWidth(e, false);
        }
    };

    const setFirstItem = () => {
        if (colonies.length > 0) {
            onClickLocation(colonies[0]);
        }
    };

    useEffect(() => {
        if (!initialValue) {
            setValue('');
        }
    }, [initialValue]);

    useEffect(() => {
        if (error) {
            setErrorText(error);
        }
    }, [error]);

    return (
        <>
            <Container data-gtm="search" onClick={(e) => handleFullWidth(e, true)} data-testid="container-search">
                <IconSearch data-testid="search-icon" data-gtm="search-icon">
                    <Icon icon="pcom-icon-search" onClick={setFirstItem} size={20} />
                </IconSearch>
                <TextField
                    dataId="input-search"
                    name="list"
                    type="text"
                    originType="pcom"
                    placeHolder={defaultPlaceholder}
                    innerLabel
                    value={value}
                    inputRef={inputRef}
                    onChange={(_, val, e) => handleOnChange(e)}
                    onKeyDown={onKeyUp}
                    onFocus={() => setIsFocus(true)}
                    autoComplete={false}
                    error={Boolean(errorText)}
                    errorMessage={errorText}
                />
                {value !== '' && isFocus && (
                    <Icon
                        data-gtm="search-clear"
                        icon="pcom-icon-close"
                        data-id="icon-close-search"
                        className="close"
                        size={16}
                        onClick={(e) => handleClearSearch(e)}
                    />
                )}
                {value && colonies.length > 0 && (
                    <List data-testid="list-result-search">
                        {colonies.map((colony, index) => (
                            <ListItem data-gtm={`item-result-${index + 1}`} key={colony.id} onClick={() => onClickLocation(colony)}>
                                {colony.label}
                            </ListItem>
                        ))}
                    </List>
                )}
            </Container>
        </>
    );
};
